import type { ExtractedFooterFields } from "~/types/components/footer";

const SOCIAL_HOST_PATTERNS = [
	/(^|\.)facebook\.com$/i,
	/(^|\.)instagram\.com$/i,
	/(^|\.)linkedin\.com$/i,
	/(^|\.)youtube\.com$/i,
	/(^|\.)tiktok\.com$/i,
	/(^|\.)twitter\.com$/i,
	/(^|\.)x\.com$/i,
	/(^|\.)pinterest\.com$/i,
	/(^|\.)threads\.net$/i,
	/(^|\.)snapchat\.com$/i,
	/(^|\.)vimeo\.com$/i
];

// Share/intent endpoints and content (posts, videos) live on social hosts but
// are not profiles: matched as the first path segment
// (youtube.com/watch, linkedin.com/shareArticle, pinterest.com/pin/create) …
const NON_PROFILE_PATH =
	/^\/(sharer|share|sharearticle|intent|dialog|plugins|watch|shorts|embed|pin|posts|hashtag|explore|search|results)(\/|\.|$)/i;
// … Instagram's own content paths — on Facebook `/p/<Name>-<id>` IS a page …
const INSTAGRAM_HOST = /(^|\.)instagram\.com$/i;
const INSTAGRAM_CONTENT_PATH = /^\/(p|reel|reels|tv|stories)(\/|$)/i;
// … or deeper, under a profile (x.com/<user>/status/…, tiktok.com/@<user>/video/…).
// Plural `videos` is deliberately absent: youtube.com/@brand/videos is the
// channel's own tab.
const NON_PROFILE_SUBPATH = /\/(status|video|posts|photos?)(\/|$)/i;

type StoryblokFooterLinkValue = {
	cached_url?: string | null;
	url?: string | null;
	linktype?: string | null;
	story?: {
		full_slug?: string | null;
	} | null;
};

/**
 * True for a profile URL on a known social network. A bare homepage
 * ("https://facebook.com") is not a profile and breaks `sameAs`.
 */
export const isSocialProfileUrl = (value: string): boolean => {
	try {
		const { hostname, pathname } = new URL(value);
		const host = hostname.replace(/^www\./i, "");
		const path = pathname.replace(/\/+$/, "");
		return (
			SOCIAL_HOST_PATTERNS.some((pattern) => pattern.test(host)) &&
			path.length > 0 &&
			!NON_PROFILE_PATH.test(path) &&
			!(INSTAGRAM_HOST.test(host) && INSTAGRAM_CONTENT_PATH.test(path)) &&
			!NON_PROFILE_SUBPATH.test(path)
		);
	} catch {
		return false;
	}
};

const resolveStoryblokLink = (
	link?: StoryblokFooterLinkValue | null
): string => {
	if (!link) return "";
	return link.url || link.cached_url || link.story?.full_slug || "";
};

const collectFooterBlocks = (footer: ExtractedFooterFields) =>
	[
		...Object.values(footer.blocks || {}),
		...Object.values(footer.combinedBlocks || {})
	].flatMap((block) => block.data || []);

const collectFooterItems = (items: unknown[] = []): Record<string, unknown>[] =>
	items.flatMap((item) => {
		if (!item || typeof item !== "object") return [];
		const record = item as Record<string, unknown>;
		const nested = Array.isArray(record.items)
			? collectFooterItems(record.items)
			: [];
		return [record, ...nested];
	});

/** Social profile links from the Storyblok footer, for Organization `sameAs`. */
export const extractSameAsLinks = (
	footer?: ExtractedFooterFields | null
): string[] => {
	if (!footer) return [];

	const blocks: Record<string, unknown>[] = [
		...collectFooterBlocks(footer),
		...collectFooterItems(footer.items || [])
	];

	const links = blocks.flatMap((block) =>
		[
			resolveStoryblokLink(
				(block.link_url || block.image_link || block.link) as
					| StoryblokFooterLinkValue
					| undefined
			),
			resolveStoryblokLink(
				block.button_link as StoryblokFooterLinkValue | undefined
			)
		].filter((link) => /^https?:\/\//i.test(link) && isSocialProfileUrl(link))
	);

	return [...new Set(links)];
};
